<template>
  <section class="boder-light-gray rounded-md border p-4 shadow-primary">
    <h1 class="text-lg">Contacts</h1>

    <div
      v-for="role in contactRoles"
      :key="role.type"
    >
      <h3 class="mb-3 mt-4 text-sm font-medium text-steel-1100">
        {{ `${role.label} Contact` }}
        <span
          v-if="role.is_required"
          class="text-steel-700"
          >(Required)</span
        >
      </h3>

      <div
        class="flex flex-row justify-between rounded-md bg-steel-100 p-4 text-sm text-steel-800"
      >
        <div class="flex flex-col gap-2">
          <p class="font-medium">
            <template v-if="isLoading">
              <div class="h-4 w-40 animate-pulse rounded bg-steel-200"></div>
            </template>
            <template v-else>
              {{ savedContacts[role.type]?.name || 'Contact Name' }}
            </template>
          </p>

          <p class="font-normal">
            <template v-if="isLoading">
              <div class="h-4 w-60 animate-pulse rounded bg-steel-200"></div>
            </template>
            <template v-else>
              {{ savedContacts[role.type]?.email_address?.value || 'Email' }}
            </template>
          </p>

          <p class="font-normal">
            <template v-if="isLoading">
              <div class="h-4 w-40 animate-pulse rounded bg-steel-200"></div>
            </template>
            <template v-else>
              {{
                formatE164WithNationalStyle(
                  savedContacts[role.type]?.phone?.phone_e164
                ) || 'Phone'
              }}
            </template>
          </p>
        </div>
        <div
          class="flex items-center gap-1"
          v-if="!isLoading"
        >
          <img
            class="cursor-pointer rounded p-1 hover:bg-steel-300"
            src="@/assets/edit-icon.svg"
            alt="Edit Icon"
            @click="openContactModal(role.type)"
          />
          <img
            v-if="!role.is_required"
            class="rounded p-1"
            :class="[
              !savedContacts[role.type]?.id
                ? 'cursor-not-allowed opacity-40'
                : 'cursor-pointer hover:bg-steel-300',
            ]"
            src="@/assets/delete-icon.svg"
            alt="Delete Icon"
            @click="handleDeleteContact(role.type)"
          />
        </div>
      </div>
    </div>

    <ContactDetailsModal
      v-if="contactDetailsModalVisible"
      :visible="contactDetailsModalVisible"
      :contact="selectedContact"
      :contacts="savedContacts"
      @close="contactDetailsModalVisible = false"
      @submit="handleSubmit"
    />
  </section>
</template>

<script setup>
import { useStore } from 'vuex';
import parsePhoneNumber from 'libphonenumber-js';
import { computed, ref, defineProps } from 'vue';
import ContactDetailsModal from '@/components/modals/ContactDetailsModal.vue';
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';

const store = useStore();
const toast = useToast();
defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const contactDetailsModalVisible = ref(false);
const selectedContact = ref(null);

const contactRoles = [
  { type: 'dispatch', label: 'Dispatch', is_required: true },
  { type: 'billing', label: 'Billing', is_required: true },
  { type: 'claims', label: 'Claims', is_required: true },
  { type: 'emergency', label: 'Emergency', is_required: false },
  { type: 'after_hours', label: 'After Hours', is_required: false },
  { type: 'owner', label: 'Owner', is_required: false },
];

const savedContacts = computed(() => store.state.contacts.savedContacts || {});

const carrierId = computed(
  () =>
    store.state.users.currentUser?.requested_carrier_id ||
    store.state.users.currentUser?.company?.carrier_id
);

const contactableId = computed(() => store.state.users?.currentUser?.id);

function openContactModal(roleType) {
  selectedContact.value = savedContacts.value[roleType] || {
    is_type: roleType,
  };
  contactDetailsModalVisible.value = true;
}

async function handleSubmit(contact) {
  console.log('handleSubmit', contact);
  const isUpdate = !!contact.existing_contact?.id;
  const type = selectedContact.value.is_type;
  const method = isUpdate ? 'patch' : 'post';

  const email_attributes = {
    is_type: type,
    value: contact.email,
    ...(contact.existing_contact?.email_address?.id && {
      id: contact.existing_contact.email_address.id,
    }),
  };

  const phone_attributes = {
    is_type: type,
    value: contact.iso?.dialCode
      ? `+${contact.iso.dialCode}${contact.phone}`
      : contact.phone,
  };

  const payload = {
    carrier_id: carrierId.value,
    name: contact.name,
    is_type: type,
    contactable_id: contactableId.value,
    contactable_type: 'OnboardingPacket',
    file_date: dayjs().format('YYYY-MM-DD'),
    email_address_attributes: email_attributes,
    phone_attributes: phone_attributes,
    ...(isUpdate && { id: contact.existing_contact.id }),
  };

  const res = await store.dispatch('contacts/createOrUpdateContact', {
    method,
    payload,
  });

  if (res?.status === 422) {
    toast.open({
      message: res?.data?.errors?.join(', ') || 'Something went wrong',
      position: 'top-right',
      type: 'error',
      duration: 1500,
    });
  } else if ([200, 201].includes(res?.status)) {
    contactDetailsModalVisible.value = false;
    await refreshContacts();
    toast.open({
      message: 'Contact saved successfully',
      position: 'top-right',
      duration: 1500,
    });
  }
}

async function handleDeleteContact(type) {
  const contact = savedContacts.value[type];
  if (!contact?.id) return;

  const payload = {
    contact: {
      id: contact.id,
      _destroy: 1,
      ...(contact.email_address?.id && {
        email_address_attributes: {
          id: contact.email_address.id,
          _destroy: 1,
        },
      }),
      ...(contact.phone?.id && {
        phone_attributes: {
          id: contact.phone.id,
          _destroy: 1,
        },
      }),
    },
  };

  const res = await store.dispatch('identity/deleteContact', {
    contact_id: contact.id,
    contact_is_type: contact.is_type,
    payload,
  });

  if ([200, 204].includes(res?.status)) {
    await refreshContacts();
    toast.open({
      message: 'Contact deleted successfully',
      position: 'top-right',
      type: 'info',
      duration: 1500,
    });
  } else {
    toast.open({
      message: 'Failed to delete contact',
      position: 'top-right',
      type: 'error',
      duration: 1500,
    });
  }
}

function formatE164WithNationalStyle(e164) {
  try {
    const phoneNumber = parsePhoneNumber(e164);
    return `${phoneNumber.countryCallingCode ? '+' + phoneNumber.countryCallingCode : ''} ${phoneNumber.formatNational()}`;
  } catch {
    return e164;
  }
}

async function refreshContacts() {
  await store.dispatch('contacts/getContactsByCarrierId', {
    page: 1,
    perPage: 50,
    carrierId: carrierId.value,
  });
}
</script>
